/**
 * Wizard — cadastro e edição do posto da AET em etapas (issue #356, D11).
 *
 * Criação: o posto nasce (POST) ao salvar a etapa 1 e fica "Em cadastro"; daí em diante cada etapa faz PUT.
 * Os quadros repetidos (atividades, cargas, equipamentos...) gravam cada item na hora, pelo subrecurso.
 * Edição: as mesmas etapas, com navegação livre. Abandonar não apaga nada: o posto segue "Em cadastro".
 */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, LinearProgress, Paper, Typography } from '@mui/material';
import { useCompanyContext } from '../../../../hooks/useCompanyContext';
import { ergonomicWorkstationService } from '../../../../services/ergonomicWorkstationService';
import type { ErgonomicOptions, ErgonomicWorkstationDetail } from '../../../../types/ergonomicWorkstation';
import type { Department } from '../../../../types/department';
import { ConfirmDialog } from './dialogs/ItemDialog';
import { ConclusionStep } from './steps/ConclusionStep';
import { FurnitureStep } from './steps/FurnitureStep';
import { OrganizationStep } from './steps/OrganizationStep';
import { PhotosStep } from './steps/PhotosStep';
import { SituationStep } from './steps/SituationStep';
import {
  EMPTY_DRAFT,
  ERGONOMIC_WIZARD_STEPS,
  WizardStepKey,
  WizardStepProps,
  WorkstationDraft,
  WorkstationErrors,
} from './Wizard.types';
import {
  draftFromWorkstation,
  draftToPayload,
  errorMessage,
  placeServerError,
  validateSituation,
  validateTexts,
} from './wizardValidation';

export interface WizardProps {
  /** Presente na edição; ausente na criação (o posto nasce ao salvar a etapa 1). */
  initialWorkstation?: ErgonomicWorkstationDetail;
  options: ErgonomicOptions | null;
  departments: readonly Department[];
  departmentsLoading: boolean;
  departmentsBlocked?: string;
  departmentsFailed?: string;
  /** Avisa a empresa escolhida na etapa 1, para quem carrega só os setores dela. */
  onCompanyChange?: (companyId: string) => void;
  onFinish: () => void;
  onCancel: () => void;
}

/** Os campos do posto que cada etapa mostra: o erro do servidor só aparece junto do campo se ele está na tela. */
const FIELDS_BY_STEP: Record<WizardStepKey, ReadonlyArray<keyof WorkstationDraft>> = {
  situation: ['companyId', 'departmentId', 'name', 'process', 'exposedCount', 'workLocation'],
  organization: [
    'productionNorm',
    'operatingMode',
    'timeRequirement',
    'workPace',
    'cycleMinutes',
    'taskContent',
    'cognitiveAspect',
    'movementRequired',
    'effortRequired',
  ],
  furniture: ['workSurface', 'reachZone', 'identifiedPosture', 'circulationArea', 'existingControls'],
  conclusion: ['riskLevel', 'riskDescription', 'actionGuidance'],
  photos: [],
};

const WHY_BY_STEP = [
  'Salvar guarda o posto e leva você à organização do trabalho. Ele fica "Em cadastro" até você concluir.',
  'Salvar guarda esta etapa e leva você ao mobiliário e equipamentos. As cargas já foram guardadas.',
  'Salvar guarda esta etapa e leva você à conclusão. Os equipamentos já foram guardados.',
  'Salvar guarda esta etapa e leva você às fotos.',
  'Concluir leva você de volta à lista de postos. Nada se perde: tudo já foi guardado.',
];

export const Wizard: React.FC<WizardProps> = ({
  initialWorkstation,
  options,
  departments,
  departmentsLoading,
  departmentsBlocked,
  departmentsFailed,
  onCompanyChange,
  onFinish,
  onCancel,
}) => {
  const [workstation, setWorkstation] = useState<ErgonomicWorkstationDetail | null>(initialWorkstation ?? null);
  const initialValues = initialWorkstation ? draftFromWorkstation(initialWorkstation) : EMPTY_DRAFT;
  const [activeStep, setActiveStep] = useState(0);
  const [values, setValues] = useState<WorkstationDraft>(initialValues);
  const [savedValues, setSavedValues] = useState<WorkstationDraft>(initialValues);
  const [errors, setErrors] = useState<WorkstationErrors>({});
  const [busy, setBusy] = useState(false);
  // Trava síncrona: dois cliques no mesmo tick leem `busy` antigo e criariam dois postos.
  const submitting = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [leaving, setLeaving] = useState<(() => void) | null>(null);

  const isEditing = Boolean(initialWorkstation);
  // Sem empresa ativa no seletor do cabeçalho, quem cadastra precisa dizer de quem é o posto.
  const { shouldShowCompanyField } = useCompanyContext();
  const askCompany = !isEditing && shouldShowCompanyField;
  const total = ERGONOMIC_WIZARD_STEPS.length;
  const stepKey = ERGONOMIC_WIZARD_STEPS[activeStep].key;
  const isLastStep = activeStep === total - 1;
  const dirty = JSON.stringify(values) !== JSON.stringify(savedValues);

  useEffect(() => {
    onCompanyChange?.(values.companyId);
  }, [onCompanyChange, values.companyId]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /** Executa a saída já; se há dados sem salvar, pergunta antes. */
  const leaveOrAsk = (action: () => void) => {
    if (dirty) setLeaving(() => action);
    else action();
  };

  const goToStep = (index: number) => {
    setError(null);
    setNotice(null);
    setErrors({});
    setActiveStep(index);
  };

  const discardAndLeave = () => {
    setValues(savedValues);
    const action = leaving;
    setLeaving(null);
    action?.();
  };

  const showServerError = (err: unknown, fallback: string) => {
    const placed = placeServerError(errorMessage(err, fallback));
    if (placed.field && FIELDS_BY_STEP[stepKey].includes(placed.field)) {
      setErrors({ [placed.field]: errorMessage(err, fallback) });
      setError('Corrija o campo marcado para continuar.');
    } else {
      setError(errorMessage(err, fallback));
    }
  };

  /** Grava o posto: POST na primeira vez, PUT depois (e só se algo mudou). `true` quando pode avançar. */
  const saveWorkstation = async (): Promise<boolean> => {
    const found = activeStep === 0 ? validateSituation(values, askCompany) : validateTexts(values);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setError('Corrija os campos marcados para continuar.');
      return false;
    }
    if (workstation !== null && !dirty) return true;

    setBusy(true);
    setError(null);
    try {
      if (workstation === null) {
        const created = await ergonomicWorkstationService.create(draftToPayload(values, true));
        setWorkstation(created);
        setSavedValues(values);
        setNotice('Posto guardado.');
      } else {
        const updated = await ergonomicWorkstationService.update(workstation.id, draftToPayload(values, false));
        setWorkstation(updated);
        setSavedValues(values);
        setNotice('Posto guardado.');
      }
      return true;
    } catch (err: unknown) {
      showServerError(err, 'Não foi possível guardar o posto. Tente de novo em alguns minutos.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handlePrimary = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setNotice(null);
    let proceed: boolean;
    try {
      proceed = await saveWorkstation();
    } finally {
      submitting.current = false;
    }
    if (!proceed) return;
    if (isLastStep) {
      onFinish();
      return;
    }
    setActiveStep(activeStep + 1);
    setErrors({});
  };

  const handleChange = (patch: Partial<WorkstationDraft>) => {
    setValues((previous) => ({ ...previous, ...patch }));
    setErrors((previous) => {
      const next = { ...previous };
      (Object.keys(patch) as Array<keyof WorkstationDraft>).forEach((key) => delete next[key]);
      return next;
    });
  };

  const handleSaved = (message: string) => setNotice(message);

  const stepProps: Omit<WizardStepProps, 'workstation'> = {
    options,
    values,
    errors,
    canEdit: true,
    disabled: busy,
    onChange: handleChange,
    onSaved: handleSaved,
  };

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <SituationStep
          {...stepProps}
          workstation={workstation}
          departments={departments}
          departmentsLoading={departmentsLoading}
          departmentsBlocked={departmentsBlocked}
          departmentsFailed={departmentsFailed}
          askCompany={askCompany}
          companyLocked={workstation !== null}
        />
      );
    }
    if (workstation === null) return null;
    const props: WizardStepProps = { ...stepProps, workstation };
    switch (activeStep) {
      case 1:
        return <OrganizationStep {...props} />;
      case 2:
        return <FurnitureStep {...props} />;
      case 3:
        return <ConclusionStep {...props} />;
      default:
        return <PhotosStep {...props} />;
    }
  };

  return (
    <Box>
      <Box
        component="nav"
        aria-label="Etapas do cadastro"
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${total}, 1fr)` }, gap: 1, mb: 1 }}
      >
        {ERGONOMIC_WIZARD_STEPS.map((step, index) => {
          const reachable = isEditing || workstation !== null || index === 0;
          const current = index === activeStep;
          const state = isEditing ? '' : current ? ' · Etapa atual' : index < activeStep ? ' · Feita' : ' · Falta';
          return (
            <Button
              key={step.key}
              variant="outlined"
              color="inherit"
              disabled={!reachable || busy}
              aria-current={current ? 'step' : undefined}
              onClick={() => !current && leaveOrAsk(() => goToStep(index))}
              sx={{
                minHeight: 64,
                flexDirection: 'column',
                alignItems: 'flex-start',
                textTransform: 'none',
                textAlign: 'left',
                borderWidth: current ? 2 : 1,
                borderColor: current ? 'text.primary' : 'divider',
                bgcolor: current ? 'action.selected' : 'transparent',
              }}
            >
              <Typography component="span" sx={{ fontSize: '1rem' }}>
                Etapa {index + 1} de {total}
                {state}
              </Typography>
              <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 700 }}>
                {step.label}
              </Typography>
            </Button>
          );
        })}
      </Box>
      <LinearProgress
        variant="determinate"
        value={((activeStep + 1) / total) * 100}
        aria-label="Progresso do cadastro"
        sx={{ mb: 3, height: 8, borderRadius: 4 }}
      />

      {notice && (
        <Alert severity="success" sx={{ fontSize: '1rem', mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ fontSize: '1rem', mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        {renderStep()}
      </Paper>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="body1" sx={{ maxWidth: 640 }}>
          {WHY_BY_STEP[activeStep]}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="text" onClick={() => leaveOrAsk(onCancel)} disabled={busy} sx={{ minHeight: 44 }}>
            Sair do cadastro
          </Button>
          {activeStep > 0 && (
            <Button
              variant="text"
              onClick={() => leaveOrAsk(() => goToStep(activeStep - 1))}
              disabled={busy}
              sx={{ minHeight: 44 }}
            >
              Voltar
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handlePrimary}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={20} color="inherit" /> : undefined}
            sx={{ minHeight: 44 }}
          >
            {isLastStep ? 'Concluir cadastro' : 'Salvar e continuar'}
          </Button>
        </Box>
      </Box>

      {leaving && (
        <ConfirmDialog
          title="Sair sem salvar?"
          message="Você digitou dados que ainda não foram salvos. Se sair agora, eles se perdem."
          confirmLabel="Sair sem salvar"
          cancelLabel="Continuar editando"
          onConfirm={discardAndLeave}
          onCancel={() => setLeaving(null)}
        />
      )}
    </Box>
  );
};

export default Wizard;
